import { FunctionComponent, useCallback, useMemo } from 'react'

import styled, { css, useTheme } from 'styled-components'

import { LIVE_EVENT_DATA_SPORT_TYPES } from '@utils/formatters/common'
import translate from '@i18n'
import useIsInCollapsibleScoreboard from '@hooks/useIsInCollapsibleScoreboard'
import useScoreboard from '@hooks/useScoreboard'
import SvgIcon from '@components/SvgIcon'

const StyledLiveDot = styled.div`
  ${({
    theme: {
      colors: { staticWhite },
      spacing: { spacing4 },
    },
  }) => css`
    background-color: ${staticWhite};
    border-radius: 50%;
    display: inline-block;
    height: 0.6em;
    line-height: 0;
    margin-right: ${spacing4};
    margin-bottom: 1px;
    opacity: 1;
    width: 0.6em;
    animation: liveFadeInOut 1.5s ease-in-out 0s infinite alternate-reverse;
    @keyframes liveFadeInOut {
      0% {
        opacity: 1;
      }
      100% {
        opacity: 0;
      }
    }
  `}
`

const StyledIcon = styled(SvgIcon)`
  ${({
    theme: {
      spacing: { spacing4 },
      typography: {
        catchword: {
          small2: { fontSize, lineHeight },
        },
      },
    },
  }) => css`
    vertical-align: top;
    margin-right: ${spacing4};
    height: calc(${fontSize} * ${lineHeight});
    width: calc(${fontSize} * ${lineHeight});
  `}
`

const StyledMatchStatus = styled.div<{
  isFinished: boolean
  isLive: boolean
  isScheduled: boolean
  isInCollapsibleScoreboard?: boolean
}>`
  ${({
    theme: {
      colors: { fillBrand, fillSport, staticWhite },
      spacing: { spacing4, spacing8 },
      typography: {
        catchword: {
          small2: { bundledCSS: small2CatchwordCSS },
        },
      },
      utils: { ellipsis },
    },
    isFinished,
    isLive,
    isScheduled,
    isInCollapsibleScoreboard,
  }) => css`
    ${small2CatchwordCSS};
    align-items: center;
    align-self: center;
    background-color: ${isLive ? fillBrand : fillSport};
    display: ${isLive || isScheduled || isFinished ? 'flex' : 'none'};
    color: ${staticWhite};
    padding: ${spacing4};
    text-align: center;
    text-transform: uppercase;
    margin-bottom: ${isInCollapsibleScoreboard ? '0' : spacing8};
    ${isInCollapsibleScoreboard && 'margin: 0 8px'};
    ${ellipsis('95%')};
  `}
`

const StyledMatchInfo = styled.div`
  ${({
    theme: {
      spacing: { spacing4, spacing8 },
      colors: { staticWhite },
    },
  }) => css`
    border-bottom-right-radius: 2px;
    display: inline !important;
    margin-left: ${spacing8};
    margin-right: -${spacing4};
    padding: 0 ${spacing4} 0 ${spacing8};
    border-left: 1px solid ${staticWhite};
  `}
`

const ScoreboardMatchStatus: FunctionComponent = () => {
  const { formattedData, sportsEventType } = useScoreboard()
  const isInCollapsibleScoreboard = useIsInCollapsibleScoreboard()
  const {
    colors: { staticWhite },
  } = useTheme()
  const { info, isFinished, isLive, isScheduled, title } = useMemo(() => {
    if (sportsEventType === LIVE_EVENT_DATA_SPORT_TYPES.FORMULA1) {
      const {
        willStart,
        isCancelled,
        isLive,
        raceTypeName,
        statusInfo,
        statusTitle,
      } = formattedData

      const isFinished = !willStart && !isLive

      return {
        info: isLive ? statusInfo : (raceTypeName ?? ''),
        isFinished: !willStart && !isLive,
        isLive,
        isScheduled: willStart,
        title: isFinished
          ? translate('liveEvent.finished')
          : isCancelled
            ? translate('liveEvent.cancelled')
            : statusTitle,
      }
    } else if (sportsEventType === LIVE_EVENT_DATA_SPORT_TYPES.SKI) {
      const {
        isBetweenRuns,
        isCancelled,
        isFinished,
        isLive,
        isPending,
        raceTypeName,
        statusInfo,
        statusTitle,
      } = formattedData

      return {
        info:
          (isLive || isBetweenRuns) && !!statusInfo
            ? statusInfo
            : !isLive && !isFinished
              ? raceTypeName
              : '',
        isFinished,
        isLive,
        isScheduled: isPending,
        title:
          isFinished || isBetweenRuns
            ? translate('liveEvent.finished')
            : isCancelled
              ? translate('liveEvent.cancelled')
              : statusTitle,
      }
    } else if (sportsEventType === LIVE_EVENT_DATA_SPORT_TYPES.TENNIS) {
      const { isLive, statusTitle, isScheduled, isCancelled, isFinished } =
        formattedData

      const isStarted = !isScheduled

      return {
        info: '',
        isFinished,
        isLive,
        isScheduled,
        title:
          !isStarted || isCancelled
            ? statusTitle
            : isFinished
              ? translate('liveEvent.finished')
              : statusTitle,
      }
    } else {
      const { isLive, statusTitle, isScheduled, isFinished } = formattedData

      return {
        info: '',
        isFinished,
        isLive,
        isScheduled,
        title: isFinished ? translate('liveEvent.finished') : statusTitle,
      }
    }
  }, [formattedData, sportsEventType])

  const getCollapsibleScoreboardTitle = useCallback((title: string): string => {
    return title.replace(/\bAb\b|\bUhr\b/g, '').trim()
  }, [])

  const shouldShowIcon = isScheduled && !isInCollapsibleScoreboard

  const collapsibleScoreboardTitle = getCollapsibleScoreboardTitle(title)

  return (
    <StyledMatchStatus
      isFinished={isFinished}
      isLive={isLive}
      isScheduled={isScheduled}
      isInCollapsibleScoreboard={isInCollapsibleScoreboard}
    >
      {isLive && <StyledLiveDot />}
      {shouldShowIcon && (
        <StyledIcon iconName="clock-three" size={12} color={staticWhite} />
      )}
      {isInCollapsibleScoreboard ? collapsibleScoreboardTitle : title}
      {info && <StyledMatchInfo>{info}</StyledMatchInfo>}
    </StyledMatchStatus>
  )
}

export default ScoreboardMatchStatus
